import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiGet } from "../utils/api";
import { getToken } from "../utils/token";
import { fmtInputDate } from "../design/domain";

/**
 * 「今天還沒建立工項」提醒 hook。
 *
 * 工項在這套系統裡就是每日日報 —— 沒有今天的工項＝今天沒有回報，
 * 但畫面上原本完全看不出來（後端的漏填偵測只在個人績效算出 missing_days，
 * 而那個數字從來沒有被顯示過），所以要主動在殼層講。
 *
 * 只有實際要 key 日報的角色會去打這支 API：PM、主管、管理員不必被日報提醒打擾，
 * 也沒道理讓每個人都多送一支請求。
 */

/** 會被提醒的角色（role.name，見 db 的 role 表：4 RD主管 / 5 RD） */
const REPORTING_ROLES = ["RD", "RD主管"];

/**
 * 角色字串來自 JWT 的 role claim，值就是主要角色的名稱（見 AuthService 登入處）。
 * 角色判定放前端做，是因為後端用 JwtSecurityToken.Claims 重讀 token 時讀不到 role
 * （見 Utility/Authorization/PermissionScope.cs 的說明）。
 */
export function isReportingRole(role) {
  return REPORTING_ROLES.includes(String(role || "").trim());
}

/** 換頁時最多每 60 秒回頭確認一次，避免每切一頁就打一次 API */
const RECHECK_MS = 60 * 1000;

/** 還沒有答案時一律不提醒。固定同一個物件，狀態沒變就不會多觸發一次 render */
const IDLE = { missing: false, date: "" };

export default function useDailyTaskReminder(role) {
  const enabled = isReportingRole(role);
  const [status, setStatus] = useState(IDLE);
  const lastCheckRef = useRef(0);
  const lastLocalDayRef = useRef("");
  const { pathname } = useLocation();

  const check = useCallback(async () => {
    if (!enabled || !getToken()) return;
    lastCheckRef.current = Date.now();
    lastLocalDayRef.current = fmtInputDate(new Date());
    try {
      const data = await apiGet("/MyTask/Today");
      /*
       * 只有「上班日 + 今天沒有工項」才提醒。
       * 已經有工項時完全不顯示 —— 一個綠色的「你今天已完成」只是每天佔掉版面，
       * 沒有人需要被告知「沒事發生」。
       */
      setStatus({
        missing: !!data && data.is_workday === true && data.has_task === false,
        // 後端判定的是台北營運日；橫幅要靠這個日期才說得出自己在講哪一天
        date: (data && data.date) || "",
      });
    } catch {
      // 查不到就當作沒事：提醒本身不該再變成一個錯誤訊息擋在使用者面前
      setStatus(IDLE);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setStatus(IDLE);
      return;
    }
    /*
     * Layout 不會因為換頁而重新掛載，所以這個 effect 的作用是「隔一段時間再確認」：
     * 使用者剛在專案頁把今天的工項開完就會換頁，橫幅因此很快會自己消失，
     * 不必等他重新整理。中間不清空 status，所以切頁時橫幅不會閃掉再出現。
     */
    const localDay = fmtInputDate(new Date());
    // 分頁跨夜開著時，昨天問到的答案已經不能用了，節流直接跳過重問
    const dayChanged = localDay !== lastLocalDayRef.current;
    if (!dayChanged && Date.now() - lastCheckRef.current < RECHECK_MS) return;
    check();
  }, [enabled, check, pathname]);

  return status;
}
